/**
 * worksheetDraft.js - the Worksheet quote's pure form logic (tested by
 * __tests__/worksheetDraft.test.js): a new draft prefilled from the
 * opportunity, the ledger's dates, the Fill bar, one day's edit, adding and
 * removing engineers. The arithmetic is NOT here: every hour and dollar comes
 * from the server (POST /api/quotes/calc, server/lib/worksheetEngine.js), the
 * same code that prices the project budget.
 *
 * The draft: { header, worksheet: { customer, start, days, billable,
 * nonBillable, fixedFee?, engineers: [{ name, level, flights, rate?, rows:
 * { 'YYYY-MM-DD': { e, t, h } } }] }, scope } - the workbook's Project Info,
 * BillRateTable and WorkSheet blocks.
 */
import { commonHeader, todayIso } from './quoteDraft';

export const MAX_WS_ENGINEERS = 12;
export const DEFAULT_DAYS = 161; // the workbook's WorkSheet rows
export const MAX_DAYS = 400;
export const LEVELS = ['Level I', 'Level II', 'Level III'];
export const FLAGS = ['Billable', 'NonBillable', 'Both Bill/UnBill', 'No'];
export const DEFAULT_TASKS = [
  'Pre-Engineering',
  'Offline Prog',
  'Offline T&L',
  'Lab',
  'Mod-Build',
  'Installation',
  'Commissioning',
  'Prod. Support',
  'Travel',
  'UNKNOWN',
];
export const PER_DAY = [
  ['hotel', 'Hotel'],
  ['transport', 'Transport'],
  ['meals', 'Meals'],
  ['parking', 'Parking'],
  ['laundry', 'Laundry'],
  ['misc', 'Misc'],
];
const DAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const txt = (v) => (v === null || v === undefined ? '' : String(v).trim());

const parse = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(txt(s));
  if (!m) return null;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCMonth() === +m[2] - 1 ? d : null;
};
export const addDays = (iso, n) => {
  const d = parse(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
/** 'Monday' ... (the WorkSheet's Day column). */
export const dayName = (iso) => {
  const d = parse(iso);
  return d ? DAY[d.getUTCDay()] : '';
};
/** 'holiday' | 'sunday' | 'saturday' | 'weekday' - which hours box a date takes. */
export function dayKind(iso, holidays) {
  if ((holidays || new Set()).has(iso)) return 'holiday';
  const g = parse(iso).getUTCDay();
  return g === 0 ? 'sunday' : g === 6 ? 'saturday' : 'weekday';
}
export const holidaySet = (list) => new Set((list || []).map((h) => txt(h && typeof h === 'object' ? h.date : h)).filter((d) => parse(d)));

/** The WorkSheet's rows: `days` dates from the start date. */
export function ledgerDates(start, days = DEFAULT_DAYS) {
  if (!parse(start)) return [];
  const n = Math.max(1, Math.min(MAX_DAYS, Math.round(Number(days)) || DEFAULT_DAYS));
  return Array.from({ length: n }, (_, i) => addDays(start, i));
}

/**
 * The rate-card customer a quote's customer is: an exact name, else the one
 * the customer's name starts with or contains ("FANUC America, Inc." is
 * FANUC), else ''.
 */
export function rateCustomerFor(settings, customer) {
  const list = ((settings && settings.customerRates) || []).map((c) => c.customer).filter(Boolean);
  const n = txt(customer).toLowerCase();
  if (!n) return '';
  return (
    list.find((c) => c.toLowerCase() === n) ||
    list.find((c) => n.startsWith(c.toLowerCase())) ||
    list.find((c) => n.includes(c.toLowerCase())) ||
    ''
  );
}

export const blankEngineer = (over = {}) => ({ name: '', level: '', flights: '', rows: {}, ...over });

/** A new Worksheet quote draft for this opportunity. */
export function newWorksheetDraft(ctx) {
  const { settings, opp, form } = ctx;
  const defs = (settings && settings.defaults) || {};
  const h = commonHeader({ ...ctx, type: 'service' });
  const { lib } = h;
  delete h.lib;
  const pick = (k, snake) => txt((form && form[k]) || (opp && (opp[k] || opp[snake])));
  return {
    header: {
      ...h,
      rev: 0,
      kicker: txt(defs.kicker) || 'SERVICE PROPOSAL',
      salutation: (lib && txt(lib.salutation)) || h.attention,
      invoicing: (lib && txt(lib.invoicing)) || txt(defs.invoicing),
      poType: txt(defs.poType) || 'T&L',
      poNumber: '',
      endUser: '',
      engManager: pick('approvalManager', 'approval_manager'),
      pm: pick('pm', 'pm'),
      fco: false,
      prevOverages: '',
      overagesNote: '',
      fcoRef: '',
    },
    worksheet: {
      customer: rateCustomerFor(settings, h.customer),
      start: todayIso(),
      days: DEFAULT_DAYS,
      billable: {},
      nonBillable: {},
      engineers: [blankEngineer()],
    },
    scope: '',
  };
}

/**
 * Fill a range of one engineer's days (the Fill bar): each date from..to
 * gets the task and flag, and the hours for its kind of day (wd Monday to
 * Friday, sat, sun; a holiday `hol`, 0 unless typed). 0 hours keeps the
 * flag - a weekend at the hotel still costs the hotel. Returns new rows.
 */
export function fillRows(rows, { from, to, task, flag, wd, sat, sun, hol }, holidays) {
  const out = { ...(rows || {}) };
  if (!parse(from) || !parse(to) || to < from) return out;
  const hs = holidays instanceof Set ? holidays : holidaySet(holidays);
  const num = (v) => Math.max(0, Math.min(24, Number(v) || 0));
  for (let d = from, i = 0; d <= to && i < MAX_DAYS; d = addDays(d, 1), i += 1) {
    const kind = dayKind(d, hs);
    const hrs = num(kind === 'holiday' ? hol : kind === 'sunday' ? sun : kind === 'saturday' ? sat : wd);
    const row = {
      e: flag === undefined ? txt(out[d] && out[d].e) : txt(flag),
      t: task === undefined ? txt(out[d] && out[d].t) : txt(task),
      h: hrs > 0 ? hrs : '',
    };
    if (!row.e && !row.t && !row.h) delete out[d];
    else out[d] = row;
  }
  return out;
}

/** One cell of one day typed: k 'e' | 't' | 'h'. An emptied day is removed. */
export function setRow(rows, date, k, v) {
  const out = { ...(rows || {}) };
  const row = { e: '', t: '', h: '', ...(out[date] || {}), [k]: v };
  if (!txt(row.e) && !txt(row.t) && !txt(row.h)) delete out[date];
  else out[date] = row;
  return out;
}

/** Clear a range of one engineer's days. */
export function clearRows(rows, from, to) {
  const out = { ...(rows || {}) };
  for (const d of Object.keys(out)) if (d >= from && d <= to) delete out[d];
  return out;
}

/** The engineers list with one engineer changed (`fn` gets and returns it). */
export const updateEngineer = (ws, i, fn) => ({ ...ws, engineers: (ws.engineers || []).map((e, j) => (j === i ? fn(e) : e)) });

/** The Fixed Fee task list a quote uses: its own, else the card's. */
export const fixedFeeOf = (ws, settings) =>
  Array.isArray(ws && ws.fixedFee)
    ? ws.fixedFee
    : (settings && settings.worksheet && settings.worksheet.fixedFeeTasks) || ['Pre-Engineering', 'Offline Prog'];
export const tasksOf = (settings) => (settings && settings.worksheet && settings.worksheet.tasks) || DEFAULT_TASKS;
export const flagsOf = (settings) => (settings && settings.worksheet && settings.worksheet.expenseFlags) || FLAGS;
